package com.evoapps.evoshift.ui.user

import com.evoapps.evoshift.domain.model.AbsenceType
import com.evoapps.evoshift.domain.model.LimitUnit
import com.evoapps.evoshift.domain.model.OccasionReason
import com.evoapps.evoshift.domain.model.WorkRequestKind
import com.evoapps.evoshift.domain.requests.LimitCheck
import com.evoapps.evoshift.domain.requests.Payer
import com.evoapps.evoshift.domain.requests.RequestCatalog
import com.evoapps.evoshift.domain.requests.RequestGroup
import com.evoapps.evoshift.domain.requests.RequestTypeInfo
import com.evoapps.evoshift.domain.usecase.requests.InvalidWorkRequestException
import com.evoapps.evoshift.ui.util.UiText
import evoshift.composeapp.generated.resources.*
import org.jetbrains.compose.resources.StringResource

/** SH-223: a counter's figure: "2", "1.5". */
fun amountText(value: Double): String {
    val rounded = kotlin.math.round(value * 10) / 10
    return if (rounded % 1.0 == 0.0) rounded.toLong().toString() else com.evoapps.evoshift.ui.finance.formatDecimal(rounded, 1)
}

/** SH-223: why a request does not fit, as the form and the snackbar say it; null when it fits. */
fun LimitCheck.toUiText(forName: String? = null): UiText? = when (this) {
    LimitCheck.Ok -> null
    is LimitCheck.Exceeds -> when {
        // SH-223: filled in for someone else: the figures, without advice meant for the person.
        forName != null -> UiText.Resource(
            if (counter.unit == LimitUnit.HOURS) Res.string.limit_exceeds_hours_for else Res.string.limit_exceeds_days_for,
            amountText(counter.remaining), amountText(counter.limit)
        )
        counter.type == AbsenceType.TIME_OFF_IN_LIEU -> UiText.Resource(Res.string.limit_exceeds_overtime, amountText(counter.remaining))
        counter.unit == LimitUnit.HOURS -> UiText.Resource(Res.string.limit_exceeds_hours, amountText(counter.remaining), amountText(counter.limit))
        else -> UiText.Resource(Res.string.limit_exceeds_days, amountText(counter.remaining), amountText(counter.limit))
    }
    is LimitCheck.UnitMismatch -> UiText.Resource(Res.string.limit_unit_days)
    is LimitCheck.TooLong -> UiText.Resource(Res.string.limit_too_long, maxDays)
    LimitCheck.Incomplete -> UiText.Resource(Res.string.limit_occasion_missing)
}

/** SH-223: what is missing from a work request. */
fun InvalidWorkRequestException.toUiText(): UiText = when (what) {
    "place" -> UiText.Resource(Res.string.place_required)
    "hours" -> UiText.Resource(Res.string.time_format_invalid)
    "dates" -> UiText.Resource(Res.string.date_end_before_start)
    else -> UiText.Resource(Res.string.error_absence_reason_too_long, com.evoapps.evoshift.domain.usecase.ReportAbsenceUseCase.REASON_MAX_LENGTH)
}

fun RequestGroup.labelRes(): StringResource = when (this) {
    RequestGroup.LEAVE -> Res.string.request_group_leave
    RequestGroup.FAMILY -> Res.string.request_group_family
    RequestGroup.HEALTH -> Res.string.request_group_health
    RequestGroup.WORK -> Res.string.request_group_work
}

/** SH-223: who pays for the time of [info]'s type, in one line. */
fun RequestTypeInfo.payRes(): StringResource = when {
    (type as? com.evoapps.evoshift.domain.requests.RequestType.Work)?.kind == WorkRequestKind.OVERTIME -> Res.string.request_pay_overtime
    type is com.evoapps.evoshift.domain.requests.RequestType.Work -> Res.string.request_pay_work
    (type as? com.evoapps.evoshift.domain.requests.RequestType.Leave)?.type.let { it == AbsenceType.SICK_LEAVE || it == AbsenceType.QUARANTINE } ->
        Res.string.request_pay_sick
    payer == Payer.ZUS -> Res.string.request_pay_zus
    payer == Payer.NOBODY -> Res.string.request_pay_none
    payShare < 1.0 -> Res.string.request_pay_employer_half
    else -> Res.string.request_pay_employer
}

fun AbsenceType.payRes(): StringResource = RequestCatalog.of(this).payRes()

fun OccasionReason.labelRes(): StringResource = when (this) {
    OccasionReason.OWN_WEDDING -> Res.string.occasion_own_wedding
    OccasionReason.CHILD_BIRTH -> Res.string.occasion_child_birth
    OccasionReason.DEATH_CLOSE_FAMILY -> Res.string.occasion_death_close
    OccasionReason.CHILD_WEDDING -> Res.string.occasion_child_wedding
    OccasionReason.DEATH_OTHER_RELATIVE -> Res.string.occasion_death_other
}
